import React, {useEffect, useMemo, useRef, useState} from 'react';
import InfoTooltip from "../InfoTooltip";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import Meeting, {requestOrUpdateInterviewForTalent} from "../../models/Meeting";
import {Img} from "react-image";
import LoadingSpinner from "../LoadingSpinner";
import User from "../../models/User";
import Form, {FormInputComponent} from "../form/Form";
import CreatableMultiSelect from "../CreatableMultiSelect";
import {useAuth} from "../../providers/AuthProvider";
import TalentCandidate from "../../models/TalentCandidate";
import UserImageCircle from "../UserImageCircle";
import Team from "../../models/Team";
import {useAPI} from "../../config/API";

const DayCell = ({date, currentMonth, timezone,selectedDate, canSelect, onDateClick, schedule}) => {
    const monthStart = TB.moment.utc(currentMonth).startOf('month');

    let formattedDate = TB.moment.utc(date).format('D');
    let sameDay = TB.moment.utc(date).isSame(selectedDate, 'day');

    let disabled = (TB.moment().utc(true).isAfter(date, 'day') && !sameDay) || !TB.moment.utc(date).isSame(monthStart, 'month')

    let hasAvailability = false;
    let availableSlots = [];

    TB.map(schedule, ({started_at, ended_at}) => {

        let startTime = TB.applyTimezoneToDateTime(started_at, timezone);

        if (startTime.format('D') === formattedDate) {
            hasAvailability = true;
            let endTime = TB.applyTimezoneToDateTime(ended_at, timezone)
            availableSlots.push(
                `${startTime.format("hh:mma")} - ${endTime.format("hh:mma")}`
            )
        }
    })

    if (!canSelect && !hasAvailability) {
        disabled = true;
    }
    return (
        <React.Fragment>
            <div id={`day-${formattedDate}`} onClick={() => onDateClick(date)}
                 className={TB.mapClass('flex-1 cell', {
                     // highlight days being currently edited
                     "selected": sameDay,
                     "availability-added": hasAvailability,
                     // Disable all days in the past or in nonactive month
                     "clickable": !sameDay && !disabled,
                     "disabled": disabled,
                     "today": sameDay,
                 })}>
                <span className="number">{formattedDate}</span>

            </div>
            {hasAvailability && document.querySelectorAll(`#day-${formattedDate}`)?.length > 0 && (
                <InfoTooltip
                    placement="top"
                    type={'gray'}
                    target={`day-${formattedDate}`}>
                    <div className="p-3 text-left time-slot-details">
                        <ul>
                            {availableSlots.map(slot => (
                                <li key={`${formattedDate}-${slot}`}>
                                    {slot}
                                </li>
                            ))}
                        </ul>
                    </div>
                </InfoTooltip>
            )}
        </React.Fragment>
    )
}

const Calendar = ({selectedDate, onSelectDate,selectedTimezone, schedule, onSetSchedule, canSelect, ...props}) => {

    const [currentMonth, setCurrentMonth] = useState(TB.moment.utc(selectedDate).startOf('month').toISOString());

    const months = useMemo(() => {
        let allMonths = [];

        for (let index = 0; index < 4; index++) {
            let month = TB.moment.utc(currentMonth).clone().add('month',index).toISOString();

            allMonths.push(month)
        }
        return allMonths
    }, []);

    const onDateClick = day => {
        console.log('onDateClick', day)

        onSelectDate(TB.moment.utc(day).clone().toISOString());
    };

    const currentIndex = useMemo(() => {
        return TB.findIndex(months, month => TB.moment.utc(month).isSame(currentMonth,'month'));
    }, [currentMonth]);

    const nextMonth = () => {
        setCurrentMonth(TB.moment.utc(currentMonth).clone().add('month', 1).toISOString())
    };

    const prevMonth = () => {
        setCurrentMonth(TB.moment.utc(currentMonth).clone().subtract('month', 1).toISOString())
    };
    const hasNextMonth = useMemo(() => {
        return !!months[currentIndex + 1];
    }, [currentIndex]);

    const hasPrevMonth = useMemo(() => {
        return !!months[currentIndex - 1]
    }, [currentIndex]);

    const CalendarHeader = useMemo(() => {
        return (
            <div className="header flex-row flex">
                <div>{TB.moment.utc(currentMonth).format("MMMM yyyy")}</div>
                <div className="col-scrollbar">
                    <button
                        type='button'
                        disabled={props.isLoading || !hasPrevMonth}
                        className={'btn btn-sm left-scroll'}
                        onClick={prevMonth}>
                        <i className={'fa fa-caret-left'}/>
                    </button>

                    <button
                        type='button'
                        className={'btn btn-sm right-scroll'}
                        disabled={props.isLoading || !hasNextMonth}
                        onClick={nextMonth}>
                        <i className={'fa fa-caret-right'}/>

                    </button>
                </div>
            </div>
        );
    }, [currentMonth])

    const CalendarWeekDays = useMemo(() => {
        const days = [];

        let startDate = TB.moment.utc(currentMonth).clone().startOf('week');

        for (let i = 0; i < 7; i++) {
            days.push(
                <div className="flex-1 text-center" key={i}>
                    {startDate.clone().add(i, 'days').format('ddd')}
                </div>
            );
        }

        return (
            <div className="days flex flex-row">
                {days}
            </div>
        )
    }, [currentMonth])


    const CalendarDays = useMemo(() => {
        const monthStart = TB.moment.utc(currentMonth).clone().startOf('month');
        const monthEnd = monthStart.clone().endOf('month');
        const startDate = monthStart.clone().startOf('week');
        const endDate = monthEnd.clone().endOf('week');

        const rows = [];

        let days = [];
        let day = startDate;

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {

                days.push(
                    <DayCell
                        key={`day-${day.toISOString()}`}
                        date={day.toISOString()}
                        timezone={selectedTimezone}
                        currentMonth={currentMonth}
                        canSelect={canSelect}
                        onDateClick={onDateClick}
                        schedule={schedule}
                        selectedDate={selectedDate}/>
                )

                day = TB.moment.utc(day).clone().add('days',1);
            }
            let week = TB.moment.utc(day).clone().startOf('isoWeek').toISOString();
            rows.push(
                <div className="flex flex-row" key={"week" + week}>
                    {days}
                </div>
            );
            days = [];
        }
        return <div className="body">{rows}</div>;
    }, [currentMonth, canSelect, schedule, selectedDate])

    return (
        <div className="calendar">
            {CalendarHeader}
            {CalendarWeekDays}
            {CalendarDays}
        </div>
    );
}

const generateTimeSlots = (date, timezone) => {
    let slots = [];

    let normalDate = TB.moment.utc(date);

    let currentDate = TB.moment.utc(normalDate.format("MM-DD-YYYY"), "MM-DD-YYYY");



    if (timezone) {
        let shiftDate = currentDate.clone().tz(timezone, false);
        shiftDate = shiftDate.set('date', currentDate.date())
            .set('month', currentDate.month())
            .set('year', currentDate.year())
        currentDate = shiftDate.clone();

    }

    let hour = 8;

    while (hour <= 20) {
        let minuteInterval = 0;

        while (minuteInterval <= 1) {

            let add = currentDate.clone()
                .set('hour', hour)
                .startOf('hour')
                .set('minute', 30 * minuteInterval)
                .startOf('minute');


            slots.push({
                started_at: add.toISOString(),
            });
            minuteInterval++;
        }
        hour++;
    }
    return slots;
}

const ProposeMeetingModal = ({onClose, project, ...props}) => {

    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();
    const {auth} = useAuth();
    const [selectedDate, setSelectedDate] = useState(() => TB.moment().utc(true).startOf('day').toISOString());

    const proposeScheduleFormRef = useRef(null);

    const forTeam = project.isService();

    const talent = useMemo(() => {
        if (forTeam) {
            return Team.normalizeScope(props.candidate, project)
        }
        return User.make(props.candidate)
    }, [props.candidate?.id]);

    const emptyMeetingModel = () => {
        let defaultInterviewName = 'Intro';
        if(auth?.clientOrg?.()?.name) {
            defaultInterviewName = `Interview: ${auth.clientOrg().name} `
            if (talent instanceof User && TB.isDefined(talent?.firstNamePlusInitial)) {
                defaultInterviewName += `and ${talent.firstNamePlusInitial?.()} `
            }else if ((talent instanceof Team) && talent?.name) {
                defaultInterviewName += `and ${talent.name} `
            }
            defaultInterviewName += `for ${project.name}`
        }

        return props.model ? Meeting.make({
            ...props.model,
            save_conference_link: false
        }) : Meeting.make({
            name: defaultInterviewName,
            available_slots: [],
            member_ids: [],
            members: [],
            duration: 30,
            conference_link: auth?.preferred_meeting_link || '',
            created_timezone: TB.getDefaultTimezoneOption()?.name || 'Eastern',
            // Save conference link by default if it hasn't already been set
            save_conference_link: !auth?.preferred_meeting_link
        })
    }

    const [meeting, setMeeting] = useState(emptyMeetingModel());

    const timeSlots = useMemo(() => {
        let timezone = TB.find(TB.TimeZoneOptions, ['name', meeting?.created_timezone])?.full_name;

        return generateTimeSlots(selectedDate,timezone)
    }, [meeting?.created_timezone, selectedDate]);


    useEffect(() => {
        return () => {
            setMeeting(emptyMeetingModel())
        };
    }, [props.candidate?.id, props.model]);

    const availableSlots = useMemo(() => meeting.groupedAvailableSlots(), [
        meeting.created_timezone,
        meeting.available_slots?.length
    ]);

    const MAX_DAY_SLOTS = 3;
    const MAX_TIME_SLOTS = 4;

    const talentImageSrc = useMemo(() => {
        if (talent?.image_path && String(talent?.image_path).includes('default-user')) {
            return null;
        }
        return talent?.image_path || TB.defaultUserProfileImagePath()
    }, [talent?.id])

    const slotsForSelectedDay = useMemo(() => {
        if (Object.keys(availableSlots).length > 0 && TB.moment.utc(selectedDate).isValid()) {
            let selectedDay = TB.moment.utc(selectedDate).startOf('day').toISOString();
            return availableSlots[selectedDay]
        }

    }, [selectedDate, availableSlots])

    const closeAndResetForm = async () => {
        await onClose()
        // await TB.wait(600);
        // setMeeting(props.model || emptyMeetingModel())
    }

    const requestOrUpdateInterviewMutation = useMutation(
        ['requestOrUpdateInterviewMutation'],
        () => requestOrUpdateInterviewForTalent(
            {
                positionId: props.position?.id,
                projectId: project.id,
                talentId: talent?.id
            },
            meeting,
            props.reschedule
        ),
        {
            onSuccess: async (updatedMeeting) => {
                setIsLoading(false);


                let successMessage = "Interview Updated";
                if (meeting.id && meeting.requestedRescheduleByTalent()) {
                    successMessage = "Interview Rescheduled";
                } else if (!meeting.id && props.position) {
                    await props.position.resetCandidateQueryDataForTalent(
                        TalentCandidate.make({
                            ...props.candidate,
                            job_candidate: {
                                ...props.candidate.job_candidate,
                                has_proposed_interview: false
                            }
                        }));

                    successMessage = "Interview Requested"
                }

                await closeAndResetForm();

                await TB.toast(successMessage, {type: 'success'})

                await TB.wait(200);

                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'fetchMeetings' &&
                            params.positionId === props.position?.id &&
                            params.projectId === project?.id
                    }
                })
                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'getConversation' &&
                            params.positionId === props.position?.id &&
                            params.candidateId === talent?.id
                    }
                })
            },
            onError: async (e) => {
                setIsLoading(false);

                console.log('onError', e)
                // await onClose();
                await TB.showServerErrorToast();


                // await TB.wait(200);
                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'fetchMeetings' &&
                            params.positionId === props.position?.id &&
                            params.projectId === project?.id
                    }
                })
            }
        }
    )

    const addDurationToTime = (time, minutes) => {
        return TB.moment.utc(time)
            .clone()
            .startOf("minute")
            .add("minutes", minutes)
            .startOf("minute")
            .toISOString()
    }
    const onMeetingChange = (updatedMeetingProps) => {
        setMeeting(Meeting.make({...meeting, ...updatedMeetingProps}))
    }

    const durations = {
        "15 min": 15,
        "30 min": 30,
        "45 min": 45,
        "1 hr": 60
    }

    const {
        data: recipients,
        isLoading: isLoadingRecipients,
        error,
        refetch,
    } = useAPI(['fetchRecipientUsers', {group: project?.client_project_group}])

    const suggestions = useMemo(() => {
        if (recipients?.length) {

            let users = User.makeMany([...recipients]);
            users = TB.filter(users, user => user.id !== auth.id && !user.isTalent());

            return TB.map(users, recipient => {
                let option = {
                    ...recipient,
                    label: recipient.fullName(),
                    value: recipient.id,
                    disabled: false
                }

                if (project?.client_project_group && !recipient.permissions.can_request_interview) {
                    option.disabled = true;
                    option.label = recipient.fullName();
                    option.format = (
                        <span>
                            {recipient.fullName()}&nbsp; <small>
                            <i className="text-danger" style={{opacity: 0.85}}>
                                Missing permission
                            </i></small>
                        </span>
                    )
                }
                return option
            });
        }
        return []
    }, [recipients, isLoadingRecipients]);

    let title = '';
    if (!meeting.id) {
        title = "Interview request";
    } else if (!!meeting.id && !!meeting.rescheduled_at && !meeting.started_at) {
        title = "Reschedule Interview";
    } else {
        title = "Edit Interview";
    }

    const canProposeTimes = useMemo(() => {
        return !meeting.id || props.reschedule;
    }, [props.reschedule, meeting.id]);

    const rawConferenceLink = TB.stripHtml(meeting.conference_link);
    const rawPersonalConferenceLink = TB.stripHtml(auth.preferred_meeting_link);


    return (
        <div className={TB.mapClass("scheduler-container", {isLoading})}>

            <h4 className="scheduler-header">{title}</h4>

            {!forTeam && (
                <div className="flex flex-row items-center justify-start">
                    <Img
                        loader={(
                            <div className="loading-user-image">
                                <LoadingSpinner/>
                            </div>
                        )}
                        unloader={<UserImageCircle user={talent} hideTooltip={true} size={55}/>}
                        src={talentImageSrc}
                        className="img-fluid img-grayscale"
                    />
                    <div className="interview-candidate-info">

                        <p>{talent.firstNamePlusInitial()}</p>

                        <div className="interview-candidate-info-options">
                            <p>
                                <i className="fa fa-info-circle"/>
                                {props.candidate?.job_title}
                            </p>

                            {talent.location && (
                                <p>
                                    <i className="fa fa-map-marker-alt"/>
                                    {talent.location.city}{!!talent.location.state && `, ${talent.location.state}`}
                                </p>
                            )}

                        </div>
                    </div>
                </div>
            )}


            <Form
                ref={proposeScheduleFormRef}
                background={'transparent'}
                minimal={true}
                onChange={(model) => {
                    let member_ids = TB.map(model?.members || [], 'value');
                    let updated = {...meeting, member_ids};

                    for (let [key, value] of Object.entries(model)) {
                        updated[key] = value;
                    }
                    return onMeetingChange(updated);
                }}
                submitButton={false}
                showUnloadWarning={false}
                model={meeting}
            >
                <div className="flex form-gap flex-row">
                    <div className="form-group flex-grow">
                        <label className="col-form-label">Interview Name {/*(Required)*/}</label>
                        <input
                            name="interview_name"
                            type="text" className="form-control" value={meeting.name}
                            onChange={e => onMeetingChange({name: e.target.value})}
                        />
                    </div>
                    <div className="form-group flex-grow-0">
                        <label className="col-form-label">Timezone</label>
                        <select
                            name="created_timezone"
                            className="form-control"
                            disabled={!canProposeTimes || isLoading}
                            onChange={e => {

                                let updates = {created_timezone: e.target.value};
                                if (meeting.available_slots?.length && e.target.value !== meeting.created_timezone) {
                                    updates.available_slots = TB.map(meeting.available_slots, slot => {
                                        return {
                                            started_at: TB.applyTimezoneToDateTime(slot.started_at, meeting.created_timezone).tz(TB.getTimezoneFromOption(e.target.value), true).utc().toISOString(),
                                            ended_at: TB.applyTimezoneToDateTime(slot.ended_at, meeting.created_timezone).tz(TB.getTimezoneFromOption(e.target.value), true).utc().toISOString(),
                                        }
                                    })
                                }

                                onMeetingChange(updates);
                            }}
                            value={meeting.created_timezone}
                        >
                            {TB.map(TB.orderBy(laravel.talent_questions.time_zones, ['is_in_us'], ['desc']), zone => (
                                <option key={zone.name} value={zone.name}>{zone.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <CreatableMultiSelect
                    name="members"
                    value={meeting.members || []}
                    disabled={isLoading}
                    className="multi-select"
                    canSelectAll={false}
                    canCreate={false}
                    label={"Invite other Wripple Users (optional)"}
                    options={suggestions}
                    required={false}
                    selectProps={{
                        formatOptionLabel: (option) => option.format || option.label,
                        isLoading: isLoadingRecipients,
                        loadingMessage: () => 'Loading members',
                        isOptionDisabled: (option) => option.disabled,
                        closeMenuOnSelect: true,
                    }}
                />

                <div className="form-group">
                    <label className="col-form-label">
                        Please add conference line details for meeting (i.e., Zoom, Teams) {/*(Required)*/}
                        {rawPersonalConferenceLink?.length > 0 && !meeting.id && rawPersonalConferenceLink === rawConferenceLink && (
                            <small className="mt-1 block"> Defaults from your profile</small>
                        )}
                    </label>
                    <FormInputComponent
                        fieldName="conference_link"
                        value={meeting.conference_link || ''}
                        disabled={isLoading}
                        rows={6}
                        label={false}
                        maxLength={2000}
                        required={true}
                        minLength={1}
                        validation={{
                            rules: {
                                maxLength: 2000,
                            },
                            errors: {
                                maxLength: TB.trans('validation.max.string', {
                                    'attribute': 'Conference details',
                                    'max': 2000
                                }),
                                minCharLength: "Conference details are required"
                            }
                        }}
                        placeholder="Required"
                        type="editor"
                        tools={['bold', 'italic', 'underline', 'link', 'ol', 'ul']}
                    />

                    {(!rawPersonalConferenceLink?.length || rawPersonalConferenceLink !== rawConferenceLink) && (
                        <div className={TB.mapClass("mt-1 form-checkbox", {
                            "disabled-checkbox": isLoading
                        })}>

                            <input type="checkbox"
                                   className="form-check-input"
                                   name="save_conference_link_checkbox"
                                   id="save_conference_link_checkbox"
                                   required={false}
                                   disabled={isLoading}
                                   onChange={event => onMeetingChange({save_conference_link: event.currentTarget.checked})}
                                   checked={meeting.save_conference_link === true}/>

                            <label className="form-check-label" htmlFor="save_conference_link_checkbox">
                                Save this meeting link as the default in my profile
                            </label>


                        </div>
                    )}
                </div>

                <div className="form-group flex flex-col">
                    <label className="col-form-label mb-2">
                        {meeting.id && meeting.started_at ? 'Expected Duration' : 'Choose a expected duration'}
                    </label>
                    <div className={TB.mapClass("duration-select-container_NEW", {
                        'no-click': meeting.id && meeting.started_at,
                    })}>
                        {TB.map(durations, (value, label) => (
                            <button
                                key={label}
                                type="button"
                                className={TB.mapClass("slot_NEW", {
                                    disabled: isLoading,
                                    selected: meeting.duration === value || !meeting.duration && label === '1 hr'
                                })}
                                onClick={() => {
                                    let updates = {duration: value};

                                    if (meeting.id && meeting.started_at) {
                                        updates.ended_at = addDurationToTime(meeting.started_at, value)
                                    }
                                    if (meeting.available_slots.length) {
                                        updates.available_slots = TB.map(meeting.available_slots, slot => {
                                            if (slot.started_at) {
                                                slot.ended_at = addDurationToTime(slot.started_at, value)
                                            }
                                            return slot;
                                        })
                                    }
                                    return onMeetingChange(updates)
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {canProposeTimes && (
                    <div className="">
                        <div className="form-group mb-0">
                            <label className="col-form-label">
                                Proposed Dates/Times
                            </label>
                        </div>
                        <div className="calendar-timeslot-container_NEW">
                            <div>
                                <p className={TB.mapClass('mb-1', {'text-danger': TB.keys(availableSlots || {}).length >= MAX_DAY_SLOTS})}>
                                    <small>
                                        {TB.keys(availableSlots || {}).length === 0 ? (
                                            'Select up to 3 dates'
                                        ) : (
                                            `${TB.keys(availableSlots || {}).length} / ${MAX_DAY_SLOTS} days selected`
                                        )}
                                    </small>
                                </p>

                                <Calendar
                                    isLoading={false}
                                    readOnly={false}
                                    isEditing={true}
                                    selectedDate={selectedDate}
                                    selectedTimezone={meeting.created_timezone}
                                    onSelectDate={day => {
                                        setSelectedDate(day);
                                    }}
                                    schedule={meeting.available_slots}
                                    onSetSchedule={(selected) => {
                                        console.log('selected', selected)

                                    }}
                                    canSelect={(TB.keys(availableSlots || {}).length || 0) < MAX_DAY_SLOTS}
                                />
                            </div>

                            <div className="time-slot-container">

                                <p className={TB.mapClass('mb-0', {'text-danger': slotsForSelectedDay?.length >= MAX_TIME_SLOTS})}>
                                    <small>
                                        {(slotsForSelectedDay?.length || 0) === 0 ? (
                                            'Select up to 4 times'
                                        ) : (
                                            `${slotsForSelectedDay?.length} / ${MAX_TIME_SLOTS} times selected`
                                        )}
                                    </small>
                                </p>

                                <div className="time-slotszz time-slots-selectt">
                                    {TB.map(timeSlots, (slot, index) => {
                                        const selected = TB.map(meeting.available_slots, 'started_at').includes(slot.started_at);

                                        const declined = (meeting.declined_slots?.findIndex(item => item.started_at === slot.started_at)) >= 0;
                                        const hasPassed = TB.moment.utc(slot.started_at).isSameOrBefore(TB.moment.utc())
                                        const disabled = (!selected && slotsForSelectedDay?.length >= MAX_TIME_SLOTS) || hasPassed

                                        const formattedDate = TB.moment.utc(slot.started_at).format("hhmma");

                                        return (
                                            <div key={slot.started_at}
                                                 className="time-slot-container_basis">
                                                <button
                                                    id={`slot-${formattedDate}`}
                                                    className={TB.mapClass("slot_NEW", {selected, disabled, declined})}
                                                    onClick={() => {
                                                        if (declined || disabled) return;
                                                        console.log('add', slot)
                                                        let addedSlot = {
                                                            ...slot,
                                                            ended_at: addDurationToTime(slot.started_at, meeting.duration)
                                                        }
                                                        let updatedSchedule = TB.xorBy([...meeting.available_slots], [addedSlot], 'started_at')
                                                        onMeetingChange({available_slots: updatedSchedule})
                                                    }}
                                                >
                                                    {TB.applyTimezoneToDateTime(slot.started_at, meeting.created_timezone).format("hh:mm a")} {TB.getTimezoneAbbrFromOption(slot.started_at,meeting.created_timezone)}
                                                </button>
                                                {!!declined && (
                                                    <InfoTooltip
                                                        placement="top"
                                                        type={'gray'}
                                                        target={`slot-${formattedDate}`}
                                                    >
                                                        Slot marked unavailable by {talent.firstNamePlusInitial()}
                                                    </InfoTooltip>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {canProposeTimes && (
                    <div className="selected-slots-container">
                        {Object.keys(availableSlots || {})?.length > 0 ? TB.map(availableSlots, (slots, day) => {
                            return (
                                <div key={"selected-" + day}>
                                    <p className="selected-slot-label">{TB.applyTimezoneToDateTime(day, meeting.created_timezone).format("ddd, MMM Do, YYYY")}</p>
                                    <div className="flex flex-wrap" style={{margin: '0 -5px'}}>
                                        {TB.map(slots, slot => {
                                            const startingTime = TB.applyTimezoneToDateTime(slot.started_at, meeting.created_timezone);
                                            const endingTime = startingTime.clone().add(meeting.duration, 'minutes');
                                            return (
                                                <div
                                                    key={slot.started_at}
                                                    className="slot_NEW slot-selected_NEW"
                                                >
                                                    {startingTime.format("h:mma")} - {endingTime.format('h:mma')} {TB.getTimezoneAbbrFromOption(slot.started_at,meeting.created_timezone)}

                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updatedSlots = meeting.available_slots.filter(item => item.started_at !== slot.started_at);
                                                            onMeetingChange({available_slots: updatedSlots})
                                                        }}
                                                    >
                                                        <i className="fa fa-times ml-2"/>
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        }) : (
                            <p className="empty-slots">Add times above to propose to the candidate</p>
                        )}
                    </div>
                )}

                <div className="submit-container">
                    <div className="">
                        <button
                            onClick={() => {
                                if (proposeScheduleFormRef?.current) {
                                    let invalid = proposeScheduleFormRef?.current?.forceValidate();

                                    if (invalid) {

                                        $('.ant-modal-wrap').animate({scrollTop: 80}, 1000);

                                        return;
                                    }
                                }

                                setIsLoading(true);
                                requestOrUpdateInterviewMutation.mutate();
                            }}
                            className="btn btn-primary"
                            disabled={!meeting.available_slots?.length || !meeting.name}
                        >
                            Submit Request
                        </button>
                    </div>
                    <div className="">
                        <button className="btn btn-outline-dark" onClick={closeAndResetForm}>
                            Cancel
                        </button>
                    </div>
                </div>

            </Form>

            {isLoading && (
                <div className="loading-container">
                    <LoadingSpinner size={"lg"}/>
                </div>
            )}
        </div>
    );
};

ProposeMeetingModal.propTypes = {};

export default ProposeMeetingModal;